import type { CSSProperties } from "react";
import type { RowData } from "@tanstack/react-table";
import type { ColumnPinData } from "../ViewTable.lib";
import { getLeafColumnHeaders } from "./getLeafColumnHeaders";
import type { ViewTableColumn, ViewTableInstance } from "./table";

const EMPTY_PINNING_STYLE: CSSProperties = {};

const getPinningStyles = <TData extends RowData>(
    column: ViewTableColumn<TData>,
): CSSProperties => {
    const isPinned = column.getIsPinned();

    if (!isPinned) {
        return EMPTY_PINNING_STYLE;
    }

    return {
        left:
            isPinned === "start" ? `${column.getStart("start")}px` : undefined,
        right: isPinned === "end" ? `${column.getAfter("end")}px` : undefined,
        position: "sticky",
    };
};

/**
 * Pinning data for every leaf column, computed once so cells don't call
 * getIsPinned(), getIsLastColumn(), getIsFirstColumn(), getPinningStyles()
 * per cell in the render loop (avoids ~1,728 TanStack accessor calls).
 */
export function getColumnPinningData<TData extends RowData>(
    table: Pick<ViewTableInstance<TData>, "getLeafHeaders">,
) {
    const columnPinningMap = new Map<string, ColumnPinData>();
    let numberOfPinnedColumns = 0;
    let pinnedLeftWidth = 0;
    let pinnedRightWidth = 0;
    for (const header of getLeafColumnHeaders(table)) {
        const column = header.column;
        const isPinned = column.getIsPinned();
        let pinSide: false | "left" | "right" = false;
        if (isPinned === "start") {
            pinnedLeftWidth += column.getSize();
            pinSide = "left";
        } else if (isPinned === "end") {
            pinnedRightWidth += column.getSize();
            pinSide = "right";
        }
        if (isPinned) {
            numberOfPinnedColumns++;
        }
        columnPinningMap.set(column.id, {
            isPinned: pinSide,
            isLastLeft: column.getIsLastColumn("start"),
            isFirstRight: column.getIsFirstColumn("end"),
            style: getPinningStyles(column),
        });
    }
    return {
        columnPinningMap,
        numberOfPinnedColumns,
        pinnedLeftWidth,
        pinnedRightWidth,
    };
}
